class WebSocketService {
  static instance = null;
  socket = null;
  isConnecting = false;
  reconnectAttempts = 0;
  maxReconnectAttempts = 10;
  reconnectDelay = 1000;
  reconnectTimer = null;
  // Multi-listener map: id -> handler function
  listeners = {};
  subscriptions = {};
  socketUrl = null;
  token = null;

  // ---------------------------------------------------------------------------
  // Legacy single-handler shim (keeps existing callers working)
  // ---------------------------------------------------------------------------
  /** @deprecated Use addListener / removeListener instead */
  get messageHandler() {
    return this.listeners['__legacy__'] || null;
  }

  /** @deprecated Use addListener / removeListener instead */
  setMessageHandler(handler) {
    this.addListener('__legacy__', handler);
  }

  /** @deprecated Use addListener / removeListener instead */
  removeMessageHandler() {
    this.removeListener('__legacy__');
  }

  // ---------------------------------------------------------------------------
  // Multi-listener API
  // ---------------------------------------------------------------------------

  /**
   * Register a named message listener.
   * @param {string} id  - Unique identifier for this listener (e.g. 'layout', 'bookManagement')
   * @param {Function} handler - WebSocket onmessage handler  (event) => void
   */
  addListener(id, handler) {
    this.listeners[id] = handler;
    this._attachDispatcher();
  }

  /**
   * Remove a previously registered listener.
   * @param {string} id - The same id used in addListener
   */
  removeListener(id) {
    delete this.listeners[id];
    this._attachDispatcher();
  }

  /**
   * Internal: attach / detach the single onmessage dispatcher.
   * Dispatches each incoming event to all registered listeners.
   */
  _attachDispatcher() {
    if (!this.socket) return;

    const ids = Object.keys(this.listeners);
    if (ids.length === 0) {
      this.socket.onmessage = null;
      return;
    }

    this.socket.onmessage = (event) => {
      // Snapshot keys at dispatch time so late removes don't throw
      Object.keys(this.listeners).forEach((key) => {
        try {
          this.listeners[key](event);
        } catch (err) {
          console.error(`[socketService] listener "${key}" threw:`, err);
        }
      });
    };
  }

  // ---------------------------------------------------------------------------
  // Connection management (unchanged)
  // ---------------------------------------------------------------------------

  static getInstance() {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  connect(socketUrl, token) {
    if (this.isConnecting) {
      return Promise.resolve(this.socket);
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return Promise.resolve(this.socket);
    }

    this.isConnecting = true;
    this.socketUrl = socketUrl;
    this.token = token;

    return new Promise((resolve, reject) => {
      try {
        const url = `${socketUrl}${token}`;
        this.socket = new WebSocket(url);

        // this.socket.onopen = () => {
        //   this.isConnecting = false;
        //   this.reconnectAttempts = 0;
        //   this.reconnectDelay = 1000;

        //   // Re-attach dispatcher so existing listeners work after reconnect
        //   this._attachDispatcher();

        //   resolve(this.socket);
        // };

        this.socket.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;

        this._attachDispatcher();


        // Restore previous subscriptions after reconnect/page refresh
        Object.values(this.subscriptions).forEach((subscription) => {
          console.log("Restoring subscription:", subscription);
          this.emit("customEvent", subscription);
        });


        resolve(this.socket);
      };

        this.socket.onerror = (error) => {
          this.isConnecting = false;
          reject(error);
        };

        this.socket.onclose = (event) => {
          this.isConnecting = false;

          if (event.code !== 1000) {
            this.reconnect();
          }
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  subscribe(topic, data = {}) {
  this.subscriptions[topic] = {
    topic,
    ...data
  };

  if (this.isSocketConnected()) {
    this.emit("customEvent", {
      topic,
      ...data
    });
  }
}


unsubscribe(topic) {
  delete this.subscriptions[topic];

  if (this.isSocketConnected()) {
    this.emit("customEvent", {
      topic,
      type: "unsubscribe"
    });
  }
}

  emit(event, data) {
    if (!this.isSocketConnected()) {
      console.warn('Socket not connected');
      return;
    }

    this.socket.send(JSON.stringify(data));
  }

  isSocketConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.close(1000);
      this.socket = null;
    }

    this.listeners = {};
    this.subscriptions = {};
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.socketUrl = null;
    this.token = null;
  }

  reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      60000
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect(this.socketUrl, this.token)
        .then(() => {
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
        })
        .catch(() => {
          this.reconnect();
        });
    }, delay);
  }
}

export default WebSocketService.getInstance();
